import { EditorAssetContentType } from "@/core/common/types";
import { UserAssetDocUploadStatus } from "@/core/common/types/assetV2";
import { DocumentReference, onSnapshot } from "firebase/firestore";

const maximumValidationWaitMs = 60_000;
const ftypBox = [0x66, 0x74, 0x79, 0x70];
const heicBrands = ["heic", "heix", "hevc", "hevx"];

const hasBytes = (bytes: Uint8Array, signature: number[], offset = 0) =>
  signature.every((byte, index) => bytes[offset + index] === byte);

// The server rejects an upload whose bytes disagree with its declared type, and callers that
// resize or re-encode an image still declare the type of the original file.
export async function getUploadContentType(data: Blob, declaredType: EditorAssetContentType) {
  const bytes = new Uint8Array(await data.slice(0, 12).arrayBuffer());
  if (hasBytes(bytes, [0x89, 0x50, 0x4e, 0x47])) {
    return EditorAssetContentType.png;
  }
  if (hasBytes(bytes, [0xff, 0xd8, 0xff])) {
    return EditorAssetContentType.jpeg;
  }
  if (hasBytes(bytes, [0x52, 0x49, 0x46, 0x46]) && hasBytes(bytes, [0x57, 0x45, 0x42, 0x50], 8)) {
    return EditorAssetContentType.webp;
  }
  return declaredType;
}

// The upload policy has no HEIC, so convert it in the one function every raw upload passes through.
export async function convertHeicUpload(data: Blob) {
  const bytes = new Uint8Array(await data.slice(0, 12).arrayBuffer());
  const brand = String.fromCharCode(...bytes.subarray(8, 12));
  const isHeic = hasBytes(bytes, ftypBox, 4) && heicBrands.includes(brand);
  if (!isHeic && data.type !== EditorAssetContentType.heic) {
    return data;
  }
  const { default: heic2any } = await import("heic2any");
  const converted = await heic2any({ blob: data, toType: "image/png" });
  return Array.isArray(converted) ? converted[0] : converted;
}

function waitForUploadValidation(assetDoc: DocumentReference) {
  return new Promise<boolean>((resolve) => {
    let unsubscribe = () => {};
    const finish = (validated: boolean) => {
      clearTimeout(timeout);
      unsubscribe();
      resolve(validated);
    };
    const timeout = setTimeout(() => finish(false), maximumValidationWaitMs);
    unsubscribe = onSnapshot(
      assetDoc,
      (snapshot) => {
        const { uploadStatus, uploadRejected } = snapshot.data() ?? {};
        if (uploadRejected) {
          finish(false);
        } else if (uploadStatus !== UserAssetDocUploadStatus.Uploading) {
          finish(uploadStatus === UserAssetDocUploadStatus.Ready);
        }
      },
      () => finish(false),
    );
  });
}

// Storage rules hide a new upload until the server has validated it, which callers cannot see.
export async function readOnceUploadIsValidated<T>(
  read: () => Promise<T>,
  assetDoc?: DocumentReference,
) {
  try {
    return await read();
  } catch (error) {
    if (
      (error as { code?: unknown })?.code !== "storage/unauthorized" ||
      !assetDoc ||
      !(await waitForUploadValidation(assetDoc))
    ) {
      throw error;
    }
    return await read();
  }
}
